import {useScopeQuery} from "@/core/store/scopeQuery";
import {defineStore, storeToRefs} from "pinia";
import {computed, shallowRef, triggerRef} from "vue";
import {useGraphql} from "@/core/graphql/service";
import saleProfileSaveMutation from "@/gql/gen/mutation/saleProfileSaveMutation";
import {useProfileModel} from "@/modules/shop/composable/useProfileModel";
import saleProfileDeleteMutation from "@/gql/gen/mutation/saleProfileDeleteMutation";
import {arrayIndex} from "@/core/util/arrayIndex";
import {OrderProfileModel} from "@/modules/shop/composable/util";
import {useVorderState} from "@/modules/shop/store/vorder-state";

const STORE_NAME = "shop-order-profile";

export const useOrderProfileStore = defineStore(STORE_NAME, () => {
    const {registerScopeQuery} = useScopeQuery();

    const attrsStore = useVorderState()

    const {currentProfileId} = storeToRefs(attrsStore)

    const profiles = shallowRef<OrderProfileModel[]>([])

    const {useMutation, responseSelection} = useGraphql();

    const profileById = computed(() => arrayIndex(profiles.value, (item) => item.source.ID))

    const currentProfile = computed(() => currentProfileId.value ? profileById.value[currentProfileId.value] : null);

    const profileSaveRequest = useMutation(saleProfileSaveMutation({
        payload: {
            __fragment: 'OrderProfileFields'
        },
        ...responseSelection()
    }))

    const profileDeleteRequest = useMutation(saleProfileDeleteMutation({
        ...responseSelection()
    }))

    const profileSave = async (profile: OrderProfileModel, apiSave = true): Promise<OrderProfileModel | false> => {
        let savedProfile: OrderProfileModel | null = null
        if (apiSave) {
            const res = await profileSaveRequest.mutate({
                id: profile.source.ID,
                attrs: profile.source.ATTR
            })
            if (res.success && res.payload) {
                savedProfile = useProfileModel(res.payload)
            }
        } else {
            savedProfile = profile
        }
        if (savedProfile) {
            const index = profiles.value.findIndex(item => item.source.ID === savedProfile?.source.ID)
            if (index > -1) {
                profiles.value[index] = savedProfile
            } else {
                profiles.value.push(savedProfile)
            }
            triggerRef(profiles)
            return savedProfile
        }
        return false
    };

    const profileDelete = async (profileId: number) => {

        const index = profiles.value.findIndex(item => item.source.ID === profileId)
        if (index > -1)
            profiles.value.splice(index, 1)

        const {success} = await profileDeleteRequest.mutate({
            id: profileId,
        })
        if (success) {
            const index = profiles.value.findIndex(item => item.source.ID === profileId)
            if (index > -1)
                profiles.value.splice(index, 1)
        }
    };

    registerScopeQuery(
        STORE_NAME,
        "user",
        {
            sale_profile_list: {
                __fragment: "OrderProfileFields",
            },
        },
        (data) => {
            if (data.sale_profile_list) profiles.value = data.sale_profile_list.map(item => useProfileModel(item))
        }
    );

    return {
        profiles,
        profileById,
        profileSaveRequest,
        profileDeleteRequest,
        profileSave,
        profileDelete,
        currentProfile
    };
});

